import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Shield, Upload, FileText, Users, Activity, LogOut, Moon, Sun, LayoutDashboard, AlertTriangle, FileBarChart, BookOpen, Menu, X, Settings } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { Button } from './ui/button';

const DashboardLayout = ({ children }) => {
  const { profile, signOut } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Close sidebar on route change (mobile)
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  // Close sidebar when clicking outside on mobile
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) setSidebarOpen(false);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleLogout = async () => {
    await signOut();
    navigate('/login');
  };

  const isActive = (path) => location.pathname === path;

  const navLink = (to, icon, label, testId) => (
    <Link to={to} key={to}>
      <button
        className={`w-full flex items-center gap-3 px-4 py-3 rounded-md text-sm transition-colors duration-150 ${
          isActive(to)
            ? 'bg-primary text-primary-foreground'
            : 'text-muted-foreground hover:bg-muted hover:text-foreground'
        }`}
        data-testid={testId}
      >
        {icon}
        {label}
      </button>
    </Link>
  );

  const SidebarContent = () => (
    <>
      <div className="p-6 border-b border-border shrink-0">
        <div className="flex items-center gap-3">
          <Shield className="w-8 h-8 text-primary" />
          <span className="text-lg font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>SecureVault</span>
        </div>
      </div>

      <nav className="flex-1 min-h-0 p-4 space-y-2 overflow-y-auto">
        {navLink('/dashboard', <LayoutDashboard className="w-5 h-5" />, 'Dashboard', 'nav-dashboard')}
        {navLink('/upload', <Upload className="w-5 h-5" />, 'Upload File', 'nav-upload')}
        {navLink('/files', <FileText className="w-5 h-5" />, 'My Files', 'nav-files')}
        {navLink('/security', <BookOpen className="w-5 h-5" />, 'Security Info', 'nav-security')}
        {navLink('/settings', <Settings className="w-5 h-5" />, 'Settings', 'nav-settings')}

        {profile?.role === 'admin' && (
          <>
            <div className="pt-4 pb-2">
              <p className="px-4 text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">Admin</p>
            </div>
            {navLink('/admin/users', <Users className="w-5 h-5" />, 'Users', 'nav-admin-users')}
            {navLink('/admin/logs', <Activity className="w-5 h-5" />, 'Activity Logs', 'nav-admin-logs')}
            {navLink('/admin/suspicious', <AlertTriangle className="w-5 h-5" />, 'Suspicious Activity', 'nav-admin-suspicious')}
            {navLink('/admin/audit', <FileBarChart className="w-5 h-5" />, 'Audit Report', 'nav-admin-audit')}
          </>
        )}
      </nav>

      <div className="p-4 border-t border-border space-y-2 shrink-0">
        <div className="px-4 py-2">
          <p className="text-sm font-medium">{profile?.name || 'User'}</p>
          <p className="text-xs text-muted-foreground font-mono truncate">{profile?.email}</p>
          {profile?.role === 'admin' && (
            <span className="inline-block mt-1 px-2 py-0.5 bg-primary/10 text-primary text-xs rounded">Admin</span>
          )}
        </div>

        <Button variant="ghost" size="sm" onClick={toggleTheme} className="w-full justify-start" data-testid="theme-toggle">
          {theme === 'dark' ? <Sun className="w-4 h-4 mr-2" /> : <Moon className="w-4 h-4 mr-2" />}
          {theme === 'dark' ? 'Light' : 'Dark'} Mode
        </Button>

        <Button
          variant="ghost"
          size="sm"
          onClick={handleLogout}
          className="w-full justify-start text-destructive hover:text-destructive hover:bg-destructive/10"
          data-testid="logout-btn"
        >
          <LogOut className="w-4 h-4 mr-2" />
          Logout
        </Button>
      </div>
    </>
  );

  return (
    <div className="h-screen flex overflow-hidden">

      {/* ── DESKTOP SIDEBAR (always visible on lg+) ── */}
      <aside className="hidden lg:flex w-64 bg-card border-r border-border flex-col shrink-0 h-screen sticky top-0 overflow-y-auto">
        <SidebarContent />
      </aside>

      {/* ── MOBILE OVERLAY ── */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* ── MOBILE SIDEBAR (slides in) ── */}
      <aside className={`fixed top-0 left-0 z-50 h-full w-64 bg-card border-r border-border flex flex-col lg:hidden transform transition-transform duration-300 ease-in-out ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        {/* Close button inside mobile sidebar */}
        <button
          className="absolute top-4 right-4 p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted"
          onClick={() => setSidebarOpen(false)}
        >
          <X className="w-5 h-5" />
        </button>
        <SidebarContent />
      </aside>

      {/* ── MAIN CONTENT ── */}
      <main className="flex-1 overflow-y-auto min-w-0">
        {/* Mobile top bar with hamburger */}
        <div className="lg:hidden flex items-center gap-3 px-4 py-3 border-b border-border bg-card sticky top-0 z-30">
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted"
            data-testid="mobile-menu-btn"
          >
            <Menu className="w-5 h-5" />
          </button>
          <Shield className="w-6 h-6 text-primary" />
          <span className="text-base font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>SecureVault</span>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {children}
        </div>
      </main>
    </div>
  );
};

export default DashboardLayout;
